/**
 * vorsteuerCockpitAccess — geteilte Zugriffslogik für das Vorsteuer-Cockpit (M8,
 * Take-away-Gate Pricing-Repackaging PR-T5, architecture-pricing-repackaging.md
 * §3.3).
 *
 * Single source of truth dafür, WER die Vorsteuer-Cockpit-Summenleiste
 * (`ExpenseSummaryBar` auf `/dashboard/ausgaben`) sehen darf. Ersetzt den
 * bisherigen Inline-`COCKPIT_ENABLED`-Modul-Konstanten in `ExpensesPage.tsx`.
 *
 * KRITISCHE Null-Delta-Invariante (Vorsteuer-Cockpit ist LIVE): solange
 * `TIER_V2_FLAG` `false` ist, liefert `canUseVorsteuerCockpit` exakt
 * `COCKPIT_FLAG` — d. h. das heutige IST-Verhalten, byte-identisch zum
 * bisherigen `COCKPIT_ENABLED`. Erst mit aktivem Master-Flag greift der
 * Owner-Tier-Gate.
 *
 * Pricing-/Tier-Kopplung (Marketing-Relaunch 2026-07-27, Betreiber-Ansage Daniel,
 * G-REL-5-Konsistenz zur neuen Landing-Preistabelle): das Vorsteuer-Cockpit wird
 * von **Premium** auf **Ultra** hochgezogen — Vorsteuer ist in der Preistabelle
 * ein Ultra-Feature (gleichgestellt zu Belegscanner/Bank-Abgleich/DATEV/UStVA/
 * Reisekosten, s. `travelExpensesAccess.ts`). Der Owner-Tier-Term lautet daher
 * `resolveTier(sellerData) === 'ultra'`. KEIN Grandfathering/Bestandsschutz:
 * das Take-away-Gate wurde nie mit einem Premium-Owner-Tier live geschaltet und
 * es gibt keine Kunden auf dem Vorsteuer-Grandfathering — der frühere
 * `isGrandfathered('vorsteuerCockpit')`-Zweig entfällt ersatzlos (Daniel-Ansage).
 *
 * Firebase-frei (nur `process.env` + pure `entitlements`-Funktionen) — aus
 * `ExpensesPage.tsx` und `config/navigation.ts` importierbar.
 *
 * Im **Advisor-Kontext** (Steuerberater im Mandanten-Tenant) trägt das Gate
 * KEINEN Owner-Tier-Term: ein StB darf NIE durch den fehlenden Premium-Status
 * seines eigenen (Kanzlei-)Kontos aus den Mandanten-Flächen ausgesperrt werden
 * (M-A1 / architecture-grace-window.md §6.3, T4b-Muster wie
 * `datevExportAccess.ts`). Das Build-Flag dominiert weiterhin — ein Advisor
 * sieht NIE ein nicht-ausgeliefertes Feature.
 *
 * @module config/vorsteuerCockpitAccess
 */

import { resolveTier, TIER_V2_FLAG, type SellerLike } from './entitlements';

/**
 * Vorsteuer-Cockpit-Build-Flag (M8, Summenleiste). Fail-closed: nur bei
 * explizitem `'true'` aktiv. Modul-Konstante, einmalig beim Bundle-Load
 * ausgewertet — identisch zur früheren `COCKPIT_ENABLED`-Konstante in
 * `ExpensesPage.tsx` (dieselbe Env-Var). LIVE `true`.
 */
export const COCKPIT_FLAG: boolean =
  process.env.REACT_APP_VORSTEUER_COCKPIT_ENABLED === 'true';

/**
 * Entscheidet fail-closed, ob ein Nutzer das Vorsteuer-Cockpit sehen darf.
 *
 * Reihenfolge (Take-away-Gate §3.3, Advisor-Bypass M-A1 §6.3):
 *   1. Build-Flag aus (`!COCKPIT_FLAG`) → nie (Feature nicht ausgeliefert) —
 *      dominiert VOR dem Advisor-Bypass (StB sieht nie Unausgeliefertes).
 *   2. Advisor-Kontext ⇒ `true` (KEIN Owner-Tier-Term — ein StB wird nie durch
 *      den Tier seines eigenen Kontos aus Mandanten-Flächen gesperrt, §6.3).
 *   3. Master-Flag aus (`!TIER_V2_FLAG`) → immer (IST-Verhalten, Null-Delta —
 *      byte-identisch zum bisherigen `COCKPIT_ENABLED`).
 *   4. Sonst: nur Ultra-Tier (`resolveTier(sellerData) === 'ultra'`). KEIN
 *      Grandfathering mehr — Bestandsschutz entfällt ersatzlos (Daniel-Ansage).
 *
 * @param sellerData - Roh-Seller-Doc-Daten aus `useAuth().sellerData` (oder
 *   `null`, solange nicht geladen). Fail-closed bei `null`/Nicht-Objekt.
 * @param isAdvisorContext - `true`, wenn der aktuelle Nutzer ein Steuerberater im
 *   Mandanten-Kontext ist (`!isOwnScope` / readOnly, §6.3) — NICHT
 *   `useIsAdvisorContext()` (eigener Kanzlei-Scope = Owner-Pfad). Dann KEIN
 *   Owner-Tier-Term (aber Build-Flag bleibt bindend).
 * @returns `true`, wenn der Nutzer das Vorsteuer-Cockpit nutzen darf.
 */
export function canUseVorsteuerCockpit(
  sellerData: SellerLike | null | undefined,
  isAdvisorContext: boolean,
): boolean {
  if (!COCKPIT_FLAG) {
    return false;
  }
  // §6.3 Advisor-Kontext: kein Owner-Tier-Term, StB nie aussperren.
  if (isAdvisorContext) {
    return true;
  }
  if (!TIER_V2_FLAG) {
    return true;
  }
  // Vorsteuer-Cockpit ist ab Marketing-Relaunch ein Ultra-Feature (G-REL-5).
  // KEIN Grandfathering — Bestandsschutz entfällt ersatzlos (Daniel-Ansage).
  return resolveTier(sellerData) === 'ultra';
}
